/* eslint-disable react/display-name */
import {
  Button,
  Grid, makeStyles, Paper,
  TablePagination, TextField, Typography
} from "@material-ui/core";
import {
  AddCircleRounded,
  CheckCircleRounded, Edit,
  Search,
  Visibility,
  VisibilityOff
} from "@material-ui/icons";
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useCommitteeList } from "../../../context/lists/committeeList";
import { useCommittees } from "../../../hooks/useCommittees";
import { useFormCombos } from "../../../hooks/useFormCombos";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { getCommittees, updateCommitteeImportance } from "../../../services/committees.service";
import { Select } from "../../Common/Select";
import { Table } from "../../Common/Table";
import Toastmessage from "../../Common/ToastMessage";
import { checkAccess } from "../utils/index";
import ModalBase from "../utils/ModalBase";
import SuccessReorderModalContent from "./SuccessReorderModalContent";
import Summary from "./Summary";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    width: "100%"
  },
  tableDetail: {
    maxWidth: "55vw"
  },
  committeeSummary: {
    width: "20vw",
    marginLeft: 20
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh"
  },
  spacer: {
    marginBottom: 20
  },
  visibleOn: {
    marginRight: 20,
    color: theme.palette.primary.main
  },
  visibleOff: {
    marginRight: 20,
    color: theme.palette.error.main
  },
  active: {
    ...theme.typography.subtitle1,
    color: "#97CA83"
  },
  inactive: {
    ...theme.typography.subtitle1,
    color: "#FE0000"
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50
  }
}));

const ListCommittees = ({ i18n, t, history, match, actionShowSpinner, location }) => {
  const classes = useStyles();
  const successReorderModalTimeoutRef = useRef(null);
  const { userAccess } = useAuth();
  const { listOptions, updateFilterParams, updatePagination } = useCommitteeList();
  const [message, setMessage] = useState({
    open: false,
    content: "",
    type: "",
    autoHideDuration: 4000,
  });

  const {
    getCommitteeSummary,
    committeeSummary,
  } = useCommittees({
    spinner: actionShowSpinner,
  });
  const { getFrequency, frequency, getStatusCommittee } = useFormCombos();

  const [createCommitteeSuccessState, setCreateCommitteeSuccessState] = useState(location?.state?.successCreateCommitee);
  const [editCommitteeSuccessState, setEditCommitteeSuccessState] = useState(location?.state?.successEditCommitee);
  const [enableCommitteeSuccessState, setEnabledCommitteeSuccessState] = useState(location?.state?.successEnabledCommitee);
  const [disableCommitteeSuccessState, setDisableCommitteeSuccessState] = useState(location?.state?.successDisabledCommitee);

  const [previewCommittee, setPreviewCommittee] = useState(false);
  const [dayOfWeekName, setDayOfWeekName] = useState("");
  const [quorum, setQuorum] = useState({});

  const [committees, setCommittees] = useState([]);
  const [totalCommittees, setTotalCommittees] = useState(0);

  const [selectedCommitteeId, setSelectedCommitteeId] = useState("");
  const [canCreateCommittee, setCanCreateCommittee] = useState(false);
  const [canUpdateCommittee, setCanUpdateCommittee] = useState(false);
  const [canUpdateCommitteeImportance, setCanUpdateCommitteeImportance] = useState(false);
  const [isReorderSuccessModalOpen, setIsReorderSuccessModalOpen] = useState(false);

  useEffect(() => {
    if (typeof location?.state?.successCreateCommitee === 'boolean' && location?.state?.successCreateCommitee) {
      setTimeout(() => setCreateCommitteeSuccessState(false), 10000)
    }
    if (typeof location?.state?.successEditCommitee === 'boolean' && location?.state?.successEditCommitee) {
      setTimeout(() => setEditCommitteeSuccessState(false), 10000)
    }
    if (typeof location?.state?.successEnabledCommitee === 'boolean' && location?.state?.successEnabledCommitee) {
      setTimeout(() => setEnabledCommitteeSuccessState(false), 10000)
    }
    if (typeof location?.state?.successDisabledCommitee === 'boolean' && location?.state?.successDisabledCommitee) {
      setTimeout(() => setDisableCommitteeSuccessState(false), 10000)
    }
  }, [location]);

  useEffect(() => {
    if (userAccess) {
      const hasAccess = checkAccess(userAccess, 'listCommittees');
      const hasAccessToCreateCommittee = checkAccess(userAccess, 'createCommitte');
      const hasAccessToUpdateCommittee = checkAccess(userAccess, 'updateCommittee');
      const hasAccessToUpdateCommitteeImportance = checkAccess(userAccess, 'changeImportanteCommittee');

      setCanCreateCommittee(hasAccessToCreateCommittee);
      setCanUpdateCommittee(hasAccessToUpdateCommittee);
      setCanUpdateCommitteeImportance(hasAccessToUpdateCommitteeImportance);

      if (!hasAccess) {
        history.push('/');
      }

      getCommittees({}, true);
    }
  }, [userAccess]);

  useEffect(() => {
    if (i18n.language) {
      getFrequency(i18n.language);
      getStatusCommittee(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    const loadCommittees = () => {
      actionShowSpinner(true);

      getCommittees({
        ...(listOptions.filterParams.committeeName && { committeeName: listOptions.filterParams.committeeName }),
        ...(listOptions.filterParams.frequency && { frequenciaId: listOptions.filterParams.frequency.id }),
        currentPage: listOptions.pagination.page,
        showDisabled: true,
        pageSize: listOptions.pagination.size
      }).then(({ data }) => {
        setCommittees(data.items);
        setTotalCommittees(data.countItems);
      }).finally(() => actionShowSpinner(false));
    };

    if (listOptions.filterParams.committeeName) {
      const delayDebounceFn = setTimeout(() => loadCommittees(), 1000)
      return () => clearTimeout(delayDebounceFn)
    }

    loadCommittees();
  }, [listOptions]);

  useEffect(() => {
    if (Object.keys(committeeSummary).length !== 0) setPreviewCommittee(true);
  }, [committeeSummary]);

  const committeesCustom = (rowData) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      {(!rowData.internal && (
        <Visibility className={classes.visibleOn} />
      )) || <VisibilityOff className={classes.visibleOff} />}
      {rowData.name}
    </div>
  );

  const closeSuccessReorderModal = () => {
    setIsReorderSuccessModalOpen(false)
  };

  const handleReOrderCommittees = async (committeeToMoveId, direction) => {
    if (successReorderModalTimeoutRef.current) {
      clearTimeout(successReorderModalTimeoutRef.current);
    }

    const { page: currentPage, size: pageSize } = listOptions.pagination;
    const numberOfPages = Math.max(Math.ceil(totalCommittees / pageSize), 1);
    const committeeIndex = committees.findIndex((committee) => committee.id === committeeToMoveId);

    const swapCommittees = (index1, index2) => {
      [committees[index1], committees[index2]] = [committees[index2], committees[index1]];
      setCommittees([...committees]);
    };

    const handleError = () => {
      if (successReorderModalTimeoutRef.current) {
        clearTimeout(successReorderModalTimeoutRef.current);
      }
      setMessage({
        open: true,
        content: t("forms.committee.reorderCommitteeListError"),
        type: "error",
        autoHideDuration: 3000,
      });
    };

    const handlePaginationUpdate = (newPage) => {
      updatePagination({ page: newPage, size: pageSize });
    };

    const triggerSuccessModal = () => {
      successReorderModalTimeoutRef.current = setTimeout(() => {
        setIsReorderSuccessModalOpen(true);
      }, 4000);
    };

    const updateAndMoveCommittee = async (newPage) => {
      try {
        await updateCommitteeImportance({ id: committeeToMoveId, direction });
        triggerSuccessModal();
      } catch {
        handleError();
      } finally {
        handlePaginationUpdate(newPage);
      }
    };

    const updateCommittee = async () => {
      try {
        await updateCommitteeImportance({ id: committeeToMoveId, direction });
        triggerSuccessModal();
      } catch {
        handleError();
      }
    };

    if (direction === "up") {
      if (committeeIndex > 0) {
        swapCommittees(committeeIndex, committeeIndex - 1);
        await updateCommittee();
      } else if (committeeIndex === 0 && currentPage > 0) {
        await updateAndMoveCommittee(currentPage - 1);
      }
    }

    if (direction === "down") {
      if (committeeIndex < committees.length - 1) {
        swapCommittees(committeeIndex, committeeIndex + 1);
        await updateCommittee();
      } else if (committeeIndex === pageSize - 1 && currentPage < numberOfPages) {
        await updateAndMoveCommittee(currentPage + 1);
      }
    }
  };



  const columns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "name",
      render: (rowData) => committeesCustom(rowData),
    },
    {
      title: t("menu.managementProfiles.email").toUpperCase(),
      field: "email",
    },
    {
      title: t("forms.committee.duration").toUpperCase(),
      field: "duraction",
    },
    {
      title: t("forms.committee.frequency").toUpperCase(),
      field: "frequency",
    },
    {
      title: t("forms.committee.subordinate").toUpperCase(),
      field: "subordinated",
    },
  ];

  return (
    <Grid container className={classes.root}>
      <ModalBase
        open={isReorderSuccessModalOpen}
        setOpen={setIsReorderSuccessModalOpen}
        maxWidth="sm"
        content={
          <SuccessReorderModalContent onClose={closeSuccessReorderModal} />
        }
      />
      <Grid container item direction="row" spacing={2} justify="space-between" className={classes.spacer}>
        <Typography variant="h4">
          {t("committees")}
        </Typography>
      </Grid>
      <Grid container item direction="row" spacing={2}>
        <Grid container item className={clsx(previewCommittee && classes.tableDetail)}>
          <Paper elevation={1} className={classes.root}>
            <Grid container spacing={3}>
              <Grid item lg={4} xs={4}>
                <TextField
                  name="committeeName"
                  id="committeeName"
                  onChange={(e) => {
                    updatePagination({ ...listOptions.pagination, page: 0, });
                    updateFilterParams({
                      ...listOptions.filterParams,
                      committeeName: e.target.value,
                    });
                  }}
                  value={listOptions.filterParams.committeeName}
                  label={t("forms.committeeName")}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item lg={4} xs={4}>
                <Select
                  id="frequency"
                  options={frequency}
                  value={listOptions.filterParams.frequency}
                  setValue={(value) => {
                    updatePagination({ ...listOptions.pagination, page: 0, });
                    updateFilterParams({
                      ...listOptions.filterParams,
                      frequency: value,
                    });
                  }}
                  label={t("forms.committee.frequency")}
                />
              </Grid>

              <Grid item lg={12} xs={12}>
                <Table
                  style={{ maxWidth: previewCommittee ? "55vw" : "100vw" }}
                  columns={columns}
                  data={committees}
                  onRowClick={(event, rowData) => {
                    if (!previewCommittee || (selectedCommitteeId && selectedCommitteeId !== rowData.id)) {
                      getCommitteeSummary(rowData.id);
                      setDayOfWeekName(rowData.daysOfWeek);
                      setQuorum({
                        instalers: rowData.countInstalers,
                        approvers: rowData.countApprovers
                      })
                      setSelectedCommitteeId(rowData.id);
                    } else {
                      setPreviewCommittee(false);
                      setSelectedCommitteeId("");
                    }
                  }}
                  actions={[
                    {
                      icon: () => <Edit />,
                      tooltip: t("edit"),
                      onClick: (_event, { id }) => {
                        history.push(`/committee/edit/${id}`);
                      },
                      disabled: !canUpdateCommittee,
                    },
                    {
                      icon: () => <Search />,
                      tooltip: t("search"),
                      onClick: (_event, { id }) => {
                        history.push(`/committee/show/${id}`);
                      },
                    },
                    {
                      icon: () => <IoChevronUp />,
                      tooltip: t("forms.committee.upImportance"),
                      onClick: (_event, { id }) => {
                        handleReOrderCommittees(id, "up");
                      },
                      disabled: !canUpdateCommitteeImportance
                    },
                    {
                      icon: () => <IoChevronDown />,
                      tooltip: t("forms.committee.downImportance"),
                      onClick: (_event, { id }) => {
                        handleReOrderCommittees(id, "down");
                      },
                      disabled: !canUpdateCommitteeImportance
                    },
                  ]}
                  pageSize={listOptions.pagination.size}
                  components={{
                    Pagination: props => {
                      return (
                        <TablePagination
                          style={{ float: "right" }}
                          rowsPerPageOptions={[5, 10, 20]}
                          labelRowsPerPage={t("table.labelRowsPerPage")}
                          rowsPerPage={listOptions.pagination.size}
                          page={listOptions.pagination.page}
                          labelDisplayedRows={(_data) => {
                            let numberOfPages = Math.ceil(totalCommittees / listOptions.pagination.size);

                            if (numberOfPages <= 1) {
                              numberOfPages = 1;
                            }

                            return `${listOptions.pagination.page + 1} ${t("table.labelDisplayedRows")} ${numberOfPages}`
                          }}
                          onPageChange={(_e, page) => {
                            updatePagination({
                              page: page,
                              size: listOptions.pagination.size
                            });
                          }}
                          onRowsPerPageChange={event => {
                            const newSize = event.target.value;

                            updatePagination({
                              page: 0,
                              size: newSize,
                            });

                            props.onChangeRowsPerPage(event);
                          }}
                          nextIconButtonProps={{
                            disabled: listOptions.pagination.page + 1 >= Math.ceil(totalCommittees / listOptions.pagination.size)
                          }}
                          backIconButtonProps={{
                            disabled: listOptions.pagination.page <= 0
                          }}
                        />
                      )
                    }
                  }}
                />
              </Grid>

              <Grid item lg={12}>
                {createCommitteeSuccessState && (
                  <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("addSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {enableCommitteeSuccessState && (
                  <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("enabledSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {disableCommitteeSuccessState && (
                  <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("disabledSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {editCommitteeSuccessState && (
                  <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                    <Grid item>
                      <CheckCircleRounded className={classes.icon} />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.label}>
                        {t("editSuccess").toUpperCase()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {
          previewCommittee &&
          <Grid container item className={classes.committeeSummary}>
            <Summary
              committeeSummary={committeeSummary}
              dayOfWeekName={dayOfWeekName}
              quorum={quorum}
            />
          </Grid>
        }
      </Grid>
      <Grid container item direction="row" justify="center">
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<AddCircleRounded style={{ fontSize: 30 }} />}
            disabled={!canCreateCommittee}
            onClick={() => history.push("/committee/new")}
          >
            {t("addCommitteeTable")}
          </Button>
        </Grid>
      </Grid>
      <Toastmessage
        message={message.content}
        type={message.type}
        open={message.open}
        autoHideDuration={message.autoHideDuration}
      />
    </Grid>
  );
};

const withRouterListCommittees = withRouter(ListCommittees);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ListCommitteesConnect = connect(
  null,
  mapDispatchToProps
)(withRouterListCommittees);

export default withTranslation()(ListCommitteesConnect);
